/**
 * PHARMAKON — Fournisseur ERP · RequireModule guard (Module 00, Appendix E.11)
 *
 * Gates a route behind a subscribed module WITHOUT ever rendering a dead page.
 * On a module miss the child still renders, but in PREVIEW mode: a token-styled
 * <PreviewBanner> is shown and a PreviewContext flag lets the page limit/blur
 * its data (real-but-limited). DESIGN_DIRECTIVE §3.9, plan §3.5.6.
 *
 * The server is authoritative: a gated API call returns HTTP 402
 * { code:"MODULE_NOT_SUBSCRIBED", module, preview:true }. Pages detect that via
 * the exported `is402PreviewError` helper and call `enterPreview()` so the rest
 * of the surface degrades gracefully. We also pre-check the user's known
 * subscribed modules (when present on the JWT user) to enter preview proactively.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore.js";
import { upgradeUrlForModule } from "@/config/fournisseur.constants.js";
import PreviewBanner from "./PreviewBanner.jsx";
// PreviewContext / usePreview / is402PreviewError live in previewGate.js so
// this module exports only the guard component (Fast Refresh).
import { PreviewContext } from "./previewGate.js";

/**
 * Resolve whether the user's subscription is known to EXCLUDE `code`.
 * Returns true only when we have positive evidence the module is missing; when
 * the subscription list is unknown we render optimistically (server gates).
 */
function knownNotSubscribed(user, code) {
  if (!code || !user) return false;
  const mods =
    user.modules ?? user.licence?.modules ?? user.subscribed_modules ?? null;
  if (!Array.isArray(mods)) return false; // unknown → optimistic
  if (mods.includes("*")) return false;
  const codes = mods.map((m) => (typeof m === "string" ? m : m?.code)).filter(Boolean);
  return !codes.includes(code);
}

export default function RequireModule({ code, children }) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [preview, setPreview] = React.useState(() => knownNotSubscribed(user, code));

  // Re-evaluate when the route's module or the user changes.
  React.useEffect(() => {
    setPreview(knownNotSubscribed(user, code));
  }, [user, code]);

  const value = React.useMemo(
    () => ({
      preview,
      module: code ?? null,
      enterPreview: () => setPreview(true),
    }),
    [preview, code],
  );

  return (
    <PreviewContext.Provider value={value}>
      {preview && (
        <PreviewBanner
          module={code}
          onUpgrade={() => navigate(upgradeUrlForModule(code))}
        />
      )}
      {children}
    </PreviewContext.Provider>
  );
}
